/**
 * Formulário tipado de parâmetros (U5 — padrão Mantine, mesma API pública): cada
 * chave tem campos nomeados em pt-BR com ajuda curta — o payload JSON é MONTADO
 * pelo formulário, nunca digitado. Faixas (INSS/IRRF) = editor de tabela com
 * colunas nomeadas, adicionar/remover linha e validação de contiguidade/cobertura
 * legível. Diff antes→depois quando pré-preenchido por "usar como base".
 */
import { useMemo, useState } from "react";
import { Alert, Button, Card, Group, NumberInput, Select, Table, Text, Title } from "@mantine/core";
import { api } from "../api";
import { avisosDe } from "./notificacoes";
import { fmtCompetencia, fmtMoeda } from "../format";
import { CompetenciaField, MoedaField, PercentField, HhMmField } from "./entradas-mantine";
import {
  SHAPES_PARAMETROS,
  shapeDaChave,
  payloadInicial,
  validarPayload,
  validarFaixas,
  edicaoParaFaixas,
  formatarPayload,
  ROTULO_COLUNA_FAIXA,
  type CampoSimples,
  type FaixaEdicao,
  type ShapeParametro,
} from "../parametros-shapes";

// ---------------------------------------------------------------------------
// Campos tipados (payload ↔ controles)
// ---------------------------------------------------------------------------

function CampoShapeInput({
  campo,
  valor,
  onChange,
}: {
  campo: CampoSimples;
  valor: unknown;
  onChange: (v: unknown) => void;
}) {
  switch (campo.kind) {
    case "moeda":
      return <MoedaField label={campo.rotulo} description={campo.ajuda} valor={(valor as number | null) ?? null} onChange={onChange} />;
    case "percentual":
      return <PercentField label={campo.rotulo} description={campo.ajuda} valor={(valor as number | null) ?? null} onChange={onChange} />;
    case "inteiro":
      return (
        <NumberInput
          label={campo.rotulo}
          description={campo.ajuda}
          value={valor === null || valor === undefined ? "" : Number(valor)}
          onChange={(v) => onChange(v === "" || v === null ? null : Number(v))}
          min={0}
          hideControls
          w={120}
        />
      );
    case "hhmm":
      return <HhMmField label={campo.rotulo} description={campo.ajuda} valor={(valor as number | null) ?? null} onChange={onChange} />;
    case "booleano":
      return (
        <Select
          label={campo.rotulo}
          description={campo.ajuda}
          data={[
            { value: "sim", label: "sim" },
            { value: "nao", label: "não" },
          ]}
          value={valor ? "sim" : "nao"}
          onChange={(v) => onChange(v === "sim")}
        />
      );
    case "selecao":
      return (
        <Select
          label={campo.rotulo}
          description={campo.ajuda}
          data={campo.opcoes.map((o) => ({ value: o.valor, label: o.rotulo }))}
          value={String(valor ?? "")}
          onChange={(v) => onChange(v)}
        />
      );
  }
}

/** Campos do payload de uma chave (inclui grupos aninhados, ex.: LC 15.270). */
export function CamposParametro({
  shape,
  payload,
  onChange,
}: {
  shape: ShapeParametro;
  payload: Record<string, unknown>;
  onChange: (p: Record<string, unknown>) => void;
}) {
  return (
    <Group align="flex-start" gap="md" wrap="wrap" mb="sm">
      {shape.campos.map((c) => {
        if (c.kind === "grupo") {
          const grupo = (payload[c.nome] ?? {}) as Record<string, unknown>;
          return (
            <Card key={c.nome} withBorder style={{ borderStyle: "dashed", flexBasis: "100%" }} padding="sm">
              <Text size="sm" fw={700} mb={2}>
                {c.rotulo}
              </Text>
              {c.ajuda && (
                <Text size="xs" c="dimmed" mb="xs">
                  {c.ajuda}
                </Text>
              )}
              <Group align="flex-start" gap="md" wrap="wrap">
                {c.campos.map((gc) => (
                  <CampoShapeInput
                    key={gc.nome}
                    campo={gc}
                    valor={grupo[gc.nome]}
                    onChange={(v) => onChange({ ...payload, [c.nome]: { ...grupo, [gc.nome]: v } })}
                  />
                ))}
              </Group>
            </Card>
          );
        }
        return <CampoShapeInput key={c.nome} campo={c} valor={payload[c.nome]} onChange={(v) => onChange({ ...payload, [c.nome]: v })} />;
      })}
    </Group>
  );
}

// ---------------------------------------------------------------------------
// Editor de faixas (INSS/IRRF)
// ---------------------------------------------------------------------------

export function FaixasEditor({
  shape,
  linhas,
  onChange,
}: {
  shape: ShapeParametro;
  linhas: FaixaEdicao[];
  onChange: (l: FaixaEdicao[]) => void;
}) {
  if (!shape.faixas) return null;
  const colunas = shape.faixas.colunas;
  const alterar = (i: number, parte: Partial<FaixaEdicao>) => onChange(linhas.map((l, j) => (j === i ? { ...l, ...parte } : l)));
  const remover = (i: number) => onChange(linhas.filter((_, j) => j !== i));
  const adicionar = () => {
    const ultima = linhas[linhas.length - 1];
    const de = ultima?.ateCentavos != null ? ultima.ateCentavos + 1 : (ultima?.deCentavos ?? -1) + 1;
    onChange([...linhas, { deCentavos: Math.max(0, de), ateCentavos: null, aliquotaPpm: null, parcelaCentavos: null }]);
  };

  return (
    <div>
      <Title order={5} mb={2}>
        {shape.faixas.rotulo}
      </Title>
      {shape.faixas.ajuda && (
        <Text size="xs" c="dimmed" mb="xs">
          {shape.faixas.ajuda}
        </Text>
      )}
      <Table withTableBorder maw={780} mb="xs">
        <Table.Thead>
          <Table.Tr>
            {colunas.map((c) => (
              <Table.Th key={c} style={{ textAlign: "right" }}>
                {ROTULO_COLUNA_FAIXA[c]}
              </Table.Th>
            ))}
            <Table.Th></Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {linhas.map((l, i) => (
            <Table.Tr key={i}>
              {colunas.includes("de") && (
                <Table.Td style={{ textAlign: "right" }}>
                  <MoedaField valor={l.deCentavos} onChange={(v) => alterar(i, { deCentavos: v })} w={110} />
                </Table.Td>
              )}
              {colunas.includes("ate") && (
                <Table.Td style={{ textAlign: "right" }}>
                  <MoedaField valor={l.ateCentavos} onChange={(v) => alterar(i, { ateCentavos: v })} w={110} placeholder="sem teto" />
                </Table.Td>
              )}
              {colunas.includes("aliquota") && (
                <Table.Td style={{ textAlign: "right" }}>
                  <PercentField valor={l.aliquotaPpm} onChange={(v) => alterar(i, { aliquotaPpm: v })} w={90} />
                </Table.Td>
              )}
              {colunas.includes("parcela") && (
                <Table.Td style={{ textAlign: "right" }}>
                  <MoedaField valor={l.parcelaCentavos} onChange={(v) => alterar(i, { parcelaCentavos: v })} w={100} />
                </Table.Td>
              )}
              <Table.Td>
                <Button type="button" variant="default" size="xs" onClick={() => remover(i)} disabled={linhas.length <= 1} aria-label={`Remover faixa ${i + 1}`}>
                  −
                </Button>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
      <Button type="button" variant="light" size="xs" onClick={adicionar}>
        + adicionar faixa
      </Button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Nova vigência (form completo — monta e envia o payload; nunca mostra JSON)
// ---------------------------------------------------------------------------

export function NovaVigenciaParametro({
  chaveInicial,
  vigenciaInicial,
  payloadExistente,
  faixasExistentes,
  onCriado,
  onErro,
}: {
  chaveInicial?: string;
  vigenciaInicial?: number;
  /** Pré-preenche o form com a vigência atual da chave (edição humana de tabela). */
  payloadExistente?: Record<string, unknown> | null;
  faixasExistentes?: FaixaEdicao[] | null;
  onCriado?: (mensagem: string, avisos: string[]) => void;
  onErro?: (e: unknown) => void;
}) {
  const [chave, setChave] = useState(chaveInicial ?? SHAPES_PARAMETROS[0]!.chave);
  const shape = shapeDaChave(chave)!;
  const [comp, setComp] = useState<number | null>(vigenciaInicial ?? null);
  const [payloads, setPayloads] = useState<Record<string, Record<string, unknown>>>({});
  const [faixasPorChave, setFaixasPorChave] = useState<Record<string, FaixaEdicao[]>>({});
  const [problemas, setProblemas] = useState<string[]>([]);
  const [enviando, setEnviando] = useState(false);

  const payload = payloads[chave] ?? payloadExistente ?? payloadInicial(shape);
  const faixas = faixasPorChave[chave] ?? faixasExistentes ?? (shape.faixas ? [] : []);
  const setPayload = (p: Record<string, unknown>) => setPayloads((a) => ({ ...a, [chave]: p }));
  const setFaixas = (f: FaixaEdicao[]) => setFaixasPorChave((a) => ({ ...a, [chave]: f }));

  /** Diff antes → depois (quando o form foi pré-preenchido por "usar como base"). */
  const diffMudancas = useMemo(() => {
    if (!payloadExistente) return [];
    const partes: string[] = [];
    const antes = new Map(formatarPayload(shape, payloadExistente).map((l) => [l.rotulo, l.valor]));
    for (const l of formatarPayload(shape, payload)) {
      if (antes.get(l.rotulo) !== undefined && antes.get(l.rotulo) !== l.valor) {
        partes.push(`${l.rotulo}: ${antes.get(l.rotulo)} → ${l.valor}`);
      }
    }
    if (shape.faixas && faixasExistentes && JSON.stringify(faixasExistentes) !== JSON.stringify(faixas)) {
      partes.push("Faixas: alteradas (confira a tabela acima)");
    }
    return partes;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payload, faixas, payloadExistente, faixasExistentes, shape]);

  async function criar() {
    const encontrados = [...validarPayload(shape, payload), ...(shape.faixas ? validarFaixas(shape, faixas) : [])];
    if (!comp) encontrados.unshift("Escolha a competência inicial (mês/ano)");
    setProblemas(encontrados);
    if (encontrados.length > 0 || !comp) return;
    setEnviando(true);
    try {
      // P2 — a resposta pode trazer `avisos` (carga histórica cobrindo meses
      // fechados: permitido — os snapshots dos fechados não mudam)
      const resposta = await api.post("/parametros", {
        chave,
        vigenciaInicio: comp,
        payload,
        ...(shape.faixas ? { faixas: edicaoParaFaixas(faixas) } : {}),
      });
      setProblemas([]);
      setPayloads((a) => {
        const copia = { ...a };
        delete copia[chave];
        return copia;
      });
      setFaixasPorChave((a) => {
        const copia = { ...a };
        delete copia[chave];
        return copia;
      });
      onCriado?.(`Nova vigência de "${shape.rotulo}" valendo a partir de ${fmtCompetencia(comp)}.`, avisosDe(resposta));
    } catch (e) {
      onErro?.(e);
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div>
      <Group align="flex-end" mb="xs" wrap="wrap">
        <Select
          label="Parâmetro"
          data={SHAPES_PARAMETROS.map((s) => ({ value: s.chave, label: s.rotulo }))}
          value={chave}
          onChange={(v) => setChave(v ?? SHAPES_PARAMETROS[0]!.chave)}
          w={320}
        />
        <CompetenciaField label="Vale a partir de" description="mês/ano da primeira competência coberta" valor={comp} onChange={setComp} />
      </Group>
      <Text size="xs" c="dimmed" mb="sm">
        {shape.descricao}
      </Text>
      <CamposParametro shape={shape} payload={payload} onChange={setPayload} />
      {shape.faixas && <FaixasEditor shape={shape} linhas={faixas} onChange={setFaixas} />}
      {problemas.length > 0 && (
        <Alert color="red" title="Revise o formulário" mt="sm" role="alert">
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {problemas.map((p, i) => (
              <li key={i}>{p}</li>
            ))}
          </ul>
        </Alert>
      )}
      {diffMudancas.length > 0 && (
        <Card withBorder bg="gray.0" padding="sm" mt="sm">
          <Text size="sm" fw={700} mb={2}>
            O que muda (antes → depois):
          </Text>
          {diffMudancas.map((d, i) => (
            <Text key={i} size="sm">
              • {d}
            </Text>
          ))}
        </Card>
      )}
      <Group mt="sm" align="center">
        <Button onClick={() => void criar()} loading={enviando}>
          Criar vigência
        </Button>
        <Text size="xs" c="dimmed">
          A vigência anterior da mesma chave é fechada na competência anterior. Retroatividade a mês fechado é permitida
          na fase de carga histórica — os meses fechados não mudam (você verá um aviso amarelo).
        </Text>
      </Group>
    </div>
  );
}

/** Resumo legível de uma faixa (para a exibição das vigências existentes). */
export function resumoFaixas(
  shape: ShapeParametro,
  faixas: { limiteInferiorCentavos: number; limiteSuperiorCentavos: number | null; aliquotaPpm: number; parcelaDeduzirCentavos: number | null }[],
): string {
  if (faixas.length === 0) return "—";
  const pct = (ppm: number) => (ppm / 10000).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return faixas
    .map((f) => {
      const de = fmtMoeda(f.limiteInferiorCentavos);
      const partes = [`de ${de}`];
      if (shape.faixas?.colunas.includes("ate")) partes.push(`até ${f.limiteSuperiorCentavos !== null ? fmtMoeda(f.limiteSuperiorCentavos) : "sem teto"}`);
      partes.push(`${pct(f.aliquotaPpm)}%`);
      if (shape.faixas?.colunas.includes("parcela") && f.parcelaDeduzirCentavos !== null) partes.push(`− ${fmtMoeda(f.parcelaDeduzirCentavos)}`);
      return partes.join(" ");
    })
    .join(" · ");
}
